import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Home, BarChart3, UserCog, FileText, Bell, ChevronDown, Users, Download, Calendar, Filter, Search, TrendingUp, Clock, FileBarChart, CheckCircle, AlertCircle, ExternalLink, Eye, Trash2, MoreVertical, Share2, KeyRound, X, AlertTriangle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export function ReportsPage() {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'alpha' | 'newest' | 'oldest' | 'size'>('newest');
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [selectedTimeframe, setSelectedTimeframe] = useState('30');
  const [openActionId, setOpenActionId] = useState<number | null>(null);
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    report: {
      id: number;
      name: string;
    } | null;
  }>({
    isOpen: false,
    report: null
  });
  const userMenuRef = useRef<HTMLDivElement>(null);
  const actionRef = useRef<HTMLDivElement>(null);
  const sortRef = useRef<HTMLDivElement>(null);
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
      if (actionRef.current && !actionRef.current.contains(event.target as Node)) {
        setOpenActionId(null);
      }
      if (sortRef.current && !sortRef.current.contains(event.target as Node)) {
        setIsSortOpen(false);
      }
    };
    if (isUserMenuOpen || openActionId !== null || isSortOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen, openActionId, isSortOpen]);
  // Mock data for reports
  const recentReports = [{
    id: 1,
    name: 'Monthly Analytics Report',
    type: 'Analytics',
    date: '2024-01-15',
    size: '2.4 MB',
    format: 'PDF',
    status: 'completed',
    downloads: 12
  }, {
    id: 2,
    name: 'Learner Progress Summary',
    type: 'Progress',
    date: '2024-01-14',
    size: '1.8 MB',
    format: 'PDF',
    status: 'completed',
    downloads: 8
  }, {
    id: 3,
    name: 'Skills Completion Report',
    type: 'Completion',
    date: '2024-01-13',
    size: '3.1 MB',
    format: 'PDF',
    status: 'completed',
    downloads: 15
  }, {
    id: 4,
    name: 'Assessment Performance',
    type: 'Assessment',
    date: '2024-01-12',
    size: '2.7 MB',
    format: 'PDF',
    status: 'completed',
    downloads: 10
  }, {
    id: 5,
    name: 'Quarterly Review Q4 2024',
    type: 'Analytics',
    date: '2024-01-10',
    size: '4.2 MB',
    format: 'PDF',
    status: 'completed',
    downloads: 20
  }];
  const reportTemplates = [{
    id: 1,
    name: 'Learner Progress Report',
    description: 'Track individual learner progress across all skills',
    icon: Users,
    color: 'from-[#30718D] to-[#265a70]',
    fields: ['Learner Name', 'Skill', 'Date Range']
  }, {
    id: 2,
    name: 'Skills Analytics',
    description: 'Comprehensive analytics for specific skills',
    icon: BarChart3,
    color: 'from-[#007558] to-[#005a44]',
    fields: ['Skill', 'Date Range', 'Metrics']
  }, {
    id: 3,
    name: 'Assessment Results',
    description: 'Detailed assessment performance and scores',
    icon: FileBarChart,
    color: 'from-purple-500 to-purple-600',
    fields: ['Assessment', 'Date Range', 'Learner Group']
  }, {
    id: 4,
    name: 'Completion Summary',
    description: 'Overview of skill completions and certifications',
    icon: CheckCircle,
    color: 'from-[#007558] to-[#005a44]',
    fields: ['Date Range', 'Skill Category']
  }];
  const filteredReports = recentReports.filter(report => {
    return report.name.toLowerCase().includes(searchQuery.toLowerCase());
  }).sort((a, b) => {
    switch (sortBy) {
      case 'alpha':
        return a.name.localeCompare(b.name);
      case 'newest':
        return new Date(b.date).getTime() - new Date(a.date).getTime();
      case 'oldest':
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      case 'size':
        {
          const sizeA = parseFloat(a.size);
          const sizeB = parseFloat(b.size);
          return sizeB - sizeA;
        }
      default:
        return 0;
    }
  });
  return <div className="flex-1 bg-gray-50 min-h-screen" data-id="element-1850">
      {/* Top Navigation */}
      <div className="bg-white border-b border-gray-200" data-id="element-1851">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1852">
          <div className="flex items-center h-16" data-id="element-1853">
            {/* Logo */}
            <div className="text-[#DB3725] font-bold text-xl" data-id="element-1854">LOGO</div>

            {/* Navigation Links */}
            <nav className="flex items-center gap-4 sm:gap-8 ml-6 sm:ml-12" data-id="element-1855">
              <Link to="/admin" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-1856">
                <Home size={18} data-id="element-1857" />
                <span className="hidden sm:inline" data-id="element-1858">Admin Hub</span>
              </Link>
              <Link to="/analytics" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-1859">
                <BarChart3 size={18} data-id="element-1860" />
                <span className="hidden sm:inline" data-id="element-1861">Analytics</span>
              </Link>
              <Link to="/feedback" className="flex items-center gap-2 text-[#30718D] border-b-2 border-[#30718D] pb-[22px] pt-[22px] transition-colors text-sm font-medium" data-id="element-1862">
                <FileText size={18} data-id="element-1863" />
                <span className="hidden sm:inline" data-id="element-1864">Reports</span>
              </Link>
              <Link to="/assessment" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-1865">
                <UserCog size={18} data-id="element-1866" />
                <span className="hidden sm:inline" data-id="element-1867">Users</span>
              </Link>
            </nav>

            {/* Right Side - User Info */}
            <div className="flex items-center gap-2 sm:gap-4 ml-auto" data-id="element-1868">
              <Link to="/alerts" className="relative p-2 text-gray-600 hover:text-gray-900 transition-colors" data-id="element-1869">
                <Bell size={20} data-id="element-1870" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-[#DB3725] rounded-full" data-id="element-1871"></span>
              </Link>
              <div className="relative" ref={userMenuRef} data-id="element-1872">
                <button onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} className="flex items-center gap-3 hover:bg-gray-50 rounded-lg px-2 py-1 transition-colors" data-id="element-1873">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-sm font-medium" data-id="element-1874">
                    LT
                  </div>
                  <span className="hidden sm:inline text-sm font-medium text-gray-900" data-id="element-1875">
                    Linda Taylor
                  </span>
                  <ChevronDown size={16} className="text-gray-600" data-id="element-1876" />
                </button>

                {isUserMenuOpen && <div className="absolute top-full right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50" data-id="element-1877">
                    <div className="py-2" data-id="element-1878">
                      <Link to="/profile" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1879">
                        <Users size={18} className="text-gray-600" data-id="element-1880" />
                        <span className="font-medium text-gray-900" data-id="element-1881">
                          Profile
                        </span>
                      </Link>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1882">
                        <Home size={18} className="text-gray-600" data-id="element-1883" />
                        <span className="font-medium text-gray-900" data-id="element-1884">
                          Learner Hub
                        </span>
                      </button>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1885">
                        <UserCog size={18} className="text-gray-600" data-id="element-1886" />
                        <span className="font-medium text-gray-900" data-id="element-1887">
                          Admin Hub
                        </span>
                      </button>
                      <Link to="/settings" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1888">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600" data-id="element-1889">
                          <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" data-id="element-1890"></path>
                          <circle cx="12" cy="12" r="3" data-id="element-1891"></circle>
                        </svg>
                        <span className="font-medium text-gray-900" data-id="element-1892">
                          Settings
                        </span>
                      </Link>
                      <div className="border-t border-gray-200 my-2" data-id="element-1893"></div>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1894">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600" data-id="element-1895">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" data-id="element-1896"></path>
                          <polyline points="16 17 21 12 16 7" data-id="element-1897"></polyline>
                          <line x1="21" y1="12" x2="9" y2="12" data-id="element-1898"></line>
                        </svg>
                        <span className="font-medium text-gray-900" data-id="element-1899">
                          Logout
                        </span>
                      </button>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8" data-id="element-1900">
        {/* Header */}
        <div className="mb-8" data-id="element-1901">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2" data-id="element-1902">
            Reports
          </h1>
          <p className="text-gray-600 text-sm sm:text-base" data-id="element-1903">
            Generate, download, and manage your learning platform reports
          </p>
        </div>

        {/* Licenses Remaining Card */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.05
      }} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 mb-8" data-id="element-1904">
          <div className="flex items-center gap-3 mb-4" data-id="element-1905">
            <div className="w-8 h-8 rounded-lg bg-[#DB3725]/10 flex items-center justify-center flex-shrink-0" data-id="element-1906">
              <KeyRound size={16} className="text-[#DB3725]" data-id="element-1907" />
            </div>
            <div data-id="element-1908">
              <div className="text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1909">
                Licenses Remaining
              </div>
              <div className="text-base font-semibold text-gray-900" data-id="element-1910">
                180{' '}
                <span className="text-sm font-medium text-gray-500" data-id="element-1911">
                  of 280
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1.5" data-id="element-1912">
            {[{
            name: 'Scrum Master Fundamentals Course',
            used: 10,
            total: 20
          }, {
            name: 'Digital Marketing Track',
            used: 3,
            total: 20
          }, {
            name: 'IT Bundle',
            used: 75,
            total: 100
          }, {
            name: 'Anatomy, Physiology and Coding I Course',
            used: 5,
            total: 20
          }, {
            name: 'AWS Solutions Architect Track',
            used: 7,
            total: 20
          }, {
            name: 'Human Resources Bundle',
            used: 80,
            total: 100
          }].map((license, idx) => {
            return <div key={idx} className="flex items-baseline gap-2 py-0.5" data-id="element-1913">
                  <span className="text-gray-300 text-xs" data-id="element-1914">•</span>
                  <span className="text-sm text-gray-600 leading-snug" data-id="element-1915">
                    <span className="font-semibold text-gray-900 tabular-nums" data-id="element-1916">
                      {license.used} of {license.total}
                    </span>{' '}
                    {license.name}
                  </span>
                </div>;
          })}
          </div>
        </motion.div>

        {/* Report Templates */}
        <div className="mb-8" data-id="element-1917">
          <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1918">
            Generate New Report
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6" data-id="element-1919">
            {reportTemplates.map((template, index) => {
            const Icon = template.icon;
            return <motion.div key={template.id} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.4 + index * 0.1
            }} className="group bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-lg transition-all cursor-pointer" data-id="element-1920">
                  <div className="flex items-start gap-4" data-id="element-1921">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${template.color} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`} data-id="element-1922">
                      <Icon size={24} className="text-white" data-id="element-1923" />
                    </div>
                    <div className="flex-1" data-id="element-1924">
                      <h3 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-[#30718D] transition-colors" data-id="element-1925">
                        {template.name}
                      </h3>
                      <p className="text-sm text-gray-600 mb-3" data-id="element-1926">
                        {template.description}
                      </p>
                      <div className="flex flex-wrap gap-2 mb-4" data-id="element-1927">
                        {template.fields.map((field, idx) => <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium" data-id="element-1928">
                            {field}
                          </span>)}
                      </div>
                      <button className="text-sm font-medium text-[#30718D] hover:text-[#265a70] transition-colors flex items-center gap-1" data-id="element-1929">
                        Generate Report
                        <ExternalLink size={14} data-id="element-1930" />
                      </button>
                    </div>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>

        {/* Recent Reports */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100" data-id="element-1931">
          <div className="p-4 sm:p-6 border-b border-gray-200" data-id="element-1932">
            <div className="flex items-center justify-between mb-4" data-id="element-1933">
              <h2 className="text-xl font-bold text-gray-900" data-id="element-1934">
                Recent Reports
              </h2>
            </div>

            {/* Search and All Filters */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3" data-id="element-1935">
              <div className="relative w-full sm:w-[512px]" data-id="element-1936">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-1937" />
                <input type="text" placeholder="Search reports..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent text-sm" data-id="element-1938" />
              </div>
              <div className="flex items-center gap-3 sm:ml-auto" data-id="element-1939">
                <select value={selectedTimeframe} onChange={e => setSelectedTimeframe(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium bg-white focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent" data-id="element-1940">
                  <option value="7" data-id="element-1941">Last 7 Days</option>
                  <option value="30" data-id="element-1942">Last 30 Days</option>
                  <option value="90" data-id="element-1943">Last 90 Days</option>
                  <option value="all" data-id="element-1944">All Time</option>
                </select>
                <div className="relative" ref={sortRef} data-id="element-1945">
                  <button onClick={() => setIsSortOpen(!isSortOpen)} className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium bg-white hover:bg-gray-50 transition-colors" data-id="element-1946">
                    Sort
                    <ChevronDown size={16} className={`text-gray-500 transition-transform ${isSortOpen ? 'rotate-180' : ''}`} data-id="element-1947" />
                  </button>
                  <AnimatePresence data-id="element-1948">
                    {isSortOpen && <motion.div initial={{
                    opacity: 0,
                    y: -4
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} exit={{
                    opacity: 0,
                    y: -4
                  }} transition={{
                    duration: 0.15
                  }} className="absolute right-0 top-full mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-20 overflow-hidden py-1" data-id="element-1949">
                        {[{
                      value: 'alpha',
                      label: 'Alpha'
                    }, {
                      value: 'newest',
                      label: 'Newest'
                    }, {
                      value: 'oldest',
                      label: 'Oldest'
                    }, {
                      value: 'size',
                      label: 'Size'
                    }].map(option => <button key={option.value} onClick={() => {
                      setSortBy(option.value as typeof sortBy);
                      setIsSortOpen(false);
                    }} className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${sortBy === option.value ? 'bg-[#30718D]/5 text-[#30718D] font-semibold' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-1950">
                            {option.label}
                            {sortBy === option.value && <CheckCircle size={14} className="ml-auto text-[#30718D]" data-id="element-1951" />}
                          </button>)}
                      </motion.div>}
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </div>

          {/* Reports Table */}
          <div className="overflow-x-auto" data-id="element-1952">
            <table className="w-full" data-id="element-1953">
              <thead data-id="element-1954">
                <tr className="border-b border-gray-200 bg-gray-50" data-id="element-1955">
                  <th className="text-left py-3 px-4 sm:px-6 font-semibold text-sm text-gray-700" data-id="element-1956">
                    Report Name
                  </th>
                  <th className="text-left py-3 px-4 sm:px-6 font-semibold text-sm text-gray-700" data-id="element-1957">
                    Date Generated
                  </th>
                  <th className="text-left py-3 px-4 sm:px-6 font-semibold text-sm text-gray-700 hidden md:table-cell" data-id="element-1958">
                    Size
                  </th>
                  <th className="text-center py-3 px-4 sm:px-6 font-semibold text-sm text-gray-700 w-20" data-id="element-1959">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-1960">
                {filteredReports.map((report, index) => <motion.tr key={report.id} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: index * 0.05
              }} className="border-b border-gray-100 hover:bg-gray-50 transition-colors" data-id="element-1961">
                    <td className="py-4 px-4 sm:px-6" data-id="element-1962">
                      <div className="flex items-center gap-2 sm:gap-3" data-id="element-1963">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-[#30718D]/10 flex items-center justify-center flex-shrink-0" data-id="element-1964">
                          <FileText size={16} className="text-[#30718D] sm:w-[18px] sm:h-[18px]" data-id="element-1965" />
                        </div>
                        <div data-id="element-1966">
                          <div className="font-medium text-gray-900 text-sm" data-id="element-1967">
                            {report.name}
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-1968">CSV</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-4 sm:px-6 text-xs sm:text-sm text-gray-600" data-id="element-1969">
                      {new Date(report.date).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                    </td>
                    <td className="py-4 px-4 sm:px-6 text-sm text-gray-600 hidden md:table-cell" data-id="element-1970">
                      {report.size}
                    </td>
                    <td className="py-4 px-4 sm:px-6" data-id="element-1971">
                      <div className="relative flex justify-center" ref={openActionId === report.id ? actionRef : undefined} data-id="element-1972">
                        <button onClick={() => setOpenActionId(openActionId === report.id ? null : report.id)} className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1973">
                          <MoreVertical size={16} data-id="element-1974" />
                        </button>
                        <AnimatePresence data-id="element-1975">
                          {openActionId === report.id && <motion.div initial={{
                        opacity: 0,
                        y: -4
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} exit={{
                        opacity: 0,
                        y: -4
                      }} transition={{
                        duration: 0.15
                      }} className="absolute right-0 top-full mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-20 overflow-hidden py-1" data-id="element-1976">
                              <button onClick={() => setOpenActionId(null)} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-1977">
                                <Download size={15} className="text-[#30718D]" data-id="element-1978" />
                                Download
                              </button>
                              <div className="border-t border-gray-100 my-1" data-id="element-1979" />
                              <button onClick={() => {
                          setOpenActionId(null);
                          setDeleteModal({
                            isOpen: true,
                            report: {
                              id: report.id,
                              name: report.name
                            }
                          });
                        }} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-[#DB3725] hover:bg-red-50 transition-colors" data-id="element-1980">
                                <Trash2 size={15} data-id="element-1981" />
                                Delete
                              </button>
                            </motion.div>}
                        </AnimatePresence>
                      </div>
                    </td>
                  </motion.tr>)}
              </tbody>
            </table>
          </div>

          {/* Delete Confirmation Modal */}
          <AnimatePresence data-id="element-1982">
            {deleteModal.isOpen && deleteModal.report && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setDeleteModal({
            isOpen: false,
            report: null
          })} data-id="element-1983">
                <motion.div initial={{
              opacity: 0,
              scale: 0.95,
              y: 20
            }} animate={{
              opacity: 1,
              scale: 1,
              y: 0
            }} exit={{
              opacity: 0,
              scale: 0.95,
              y: 20
            }} className="bg-white rounded-xl shadow-2xl max-w-md w-full" onClick={e => e.stopPropagation()} data-id="element-1984">
                  <div className="flex items-center justify-between p-6 border-b border-gray-100" data-id="element-1985">
                    <div className="flex items-center gap-3" data-id="element-1986">
                      <div className="w-12 h-12 rounded-lg bg-[#DB3725]/10 flex items-center justify-center" data-id="element-1987">
                        <AlertTriangle size={24} className="text-[#DB3725]" data-id="element-1988" />
                      </div>
                      <h2 className="text-xl font-bold text-gray-900" data-id="element-1989">
                        Delete Report
                      </h2>
                    </div>
                    <button onClick={() => setDeleteModal({
                  isOpen: false,
                  report: null
                })} className="text-sm font-semibold text-gray-700 hover:text-gray-900 underline transition-colors" data-id="element-1990">
                      Close
                    </button>
                  </div>
                  <div className="p-6" data-id="element-1991">
                    <p className="text-gray-600 mb-2" data-id="element-1992">
                      Are you sure you want to delete this report?
                    </p>
                    <p className="text-sm font-semibold text-gray-900 bg-gray-50 rounded-lg px-4 py-3 border border-gray-200" data-id="element-1993">
                      {deleteModal.report.name}
                    </p>
                    <p className="text-sm text-gray-500 mt-3" data-id="element-1994">
                      This action cannot be undone. The report will be
                      permanently removed.
                    </p>
                  </div>
                  <div className="flex gap-3 p-6 border-t border-gray-100" data-id="element-1995">
                    <button onClick={() => setDeleteModal({
                  isOpen: false,
                  report: null
                })} className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium" data-id="element-1996">
                      Cancel
                    </button>
                    <button onClick={() => setDeleteModal({
                  isOpen: false,
                  report: null
                })} className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors font-medium" data-id="element-1997">
                      <Trash2 size={18} data-id="element-1998" />
                      Delete
                    </button>
                  </div>
                </motion.div>
              </motion.div>}
          </AnimatePresence>

          {filteredReports.length === 0 && <div className="p-12 text-center" data-id="element-1999">
              <FileText size={48} className="mx-auto text-gray-400 mb-4" data-id="element-2000" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-2001">
                No reports found
              </h3>
              <p className="text-gray-600" data-id="element-2002">
                Try adjusting your search or filter criteria
              </p>
            </div>}
        </div>
      </div>
    </div>;
}